import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthSession,
  LoginErrorResponse,
  LoginRequest,
  LoginResponse,
} from '../models/auth.model';

const AUTH_KEY = 'care_admin_auth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly isAuthenticatedSignal = signal(this.hasStoredSession());

  readonly isAuthenticated = this.isAuthenticatedSignal.asReadonly();

  login(email: string, password: string): Observable<LoginResponse> {
    const body: LoginRequest = {
      email: email.trim(),
      password,
    };

    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/admin/login`, body)
      .pipe(
        tap((response) => {
          if (response.success && response.token) {
            this.persistSession({
              token: response.token,
              email: response.admin?.email ?? email.trim(),
            });
          }
        }),
        catchError((error: HttpErrorResponse) => {
          const message =
            (error.error as LoginErrorResponse)?.message ??
            error.message ??
            'Login failed. Please try again.';
          return throwError(() => new Error(message));
        })
      );
  }

  logout(): void {
    sessionStorage.removeItem(AUTH_KEY);
    this.isAuthenticatedSignal.set(false);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return this.getSession()?.token ?? null;
  }

  getSession(): AuthSession | null {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      return null;
    }
  }

  private persistSession(session: AuthSession): void {
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(session));
    this.isAuthenticatedSignal.set(true);
  }

  private hasStoredSession(): boolean {
    return !!this.getSession()?.token;
  }
}
